import { FC, Fragment } from 'react';
import {
  ActionsMenu,
  Button,
  Link,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Delete, Edit } from '@mui/icons-material';
import {
  useRecognitionAllowanceRules,
  useRemoveRecognitionAllowanceRule,
} from 'store/recognition-allowances';
import { useModalContext } from 'store/modal';
import { useConfirm } from 'store/confirm';
import { usePointsLabel } from 'store/recognitions';
import { usePredefinedToasts } from 'utils/Toast';
import { ROUTES } from 'config';
import { useMe } from 'state/Administrators';
import { AllowanceRuleModal } from './atoms';

export const RecognitionsRulesList: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const { hasEditPermission, hasViewPermission } = useMe();
  const predefinedToasts = usePredefinedToasts();

  const pointsLabel = usePointsLabel();

  const { data: allowanceRules, isLoading } = useRecognitionAllowanceRules();

  const { mutate: removeRule, isLoading: isRemoving } =
    useRemoveRecognitionAllowanceRule();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack spacing={2} p={1}>
            <Typography variant="h2">
              {t('recognition.allowanceRulesTab')}
            </Typography>
            <Typography variant="p1" color="textSecondary">
              {t('recognitions.rules.info.message')}
            </Typography>
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('recognition.newAllowanceRule'),
                  color: 'primary',
                  disabled: !hasEditPermission(['employeeRecognition']),
                  onClick: () => {
                    openModal({
                      component: AllowanceRuleModal,
                    });
                  },
                },
              ]}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={allowanceRules ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'employeeGroups',
            header: t('recognition.allowanceRulesListEmployeeGroups'),
            cell: ({ row: { original: rule } }) => (
              <Stack>
                {rule.employeeGroups.map((group) =>
                  hasViewPermission(['groups']) &&
                  hasViewPermission(['benefits', 'employees']) ? (
                    <Link
                      label={group.name}
                      to={{
                        pathname: ROUTES.employeeGroups.details.replace(
                          ':id',
                          group.id,
                        ),
                      }}
                      key={group.id}
                    />
                  ) : (
                    <Fragment key={group.id}>{group.name}</Fragment>
                  ),
                )}
              </Stack>
            ),
          },
          {
            id: 'points',
            header: t('recognition.allowanceRulesListPoints', {
              pointsName: pointsLabel,
            }),
            cell: ({ row: { original: rule } }) => rule.points,
            meta: { align: 'right' },
          },
          {
            id: 'frequency',
            header: t('recognition.allowanceRulesListFrequency'),
            cell: ({ row: { original: rule } }) =>
              t(`recognition.allowanceRuleFrequency-${rule.frequency}`),
          },
          {
            id: 'actions',
            meta: { align: 'right' },
            cell: ({ row: { original: rule } }) => (
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button
                  icon={Edit}
                  label={t('common.edit')}
                  disabled={!hasEditPermission(['employeeRecognition'])}
                  variant="neutral-outline"
                  size="sm"
                  onClick={() => {
                    openModal({
                      component: AllowanceRuleModal,
                      props: {
                        allowanceRuleId: rule.id,
                        initialValues: {
                          employeeGroups: rule.employeeGroups.map(
                            (group) => group.id,
                          ),
                          points: rule.points,
                          frequency: rule.frequency,
                          assignAllowanceOnCreation: true,
                        },
                      },
                    });
                  }}
                />
                <Button
                  icon={Delete}
                  label={t('actions.delete')}
                  variant="neutral-outline"
                  size="sm"
                  disabled={
                    isRemoving || !hasEditPermission(['employeeRecognition'])
                  }
                  onClick={async () => {
                    const { confirmed } = await confirmation(
                      t('recognition.removeAllowanceRuleWarning', {
                        groups: rule.employeeGroups
                          .map((group) => group.name)
                          .join(', '),
                      }),
                      undefined,
                      'danger',
                    );

                    if (confirmed) {
                      removeRule(
                        { allowanceRuleId: rule.id },
                        {
                          onSuccess: () => {
                            predefinedToasts.success.deleted();
                          },
                        },
                      );
                    }
                  }}
                />
              </Stack>
            ),
          },
        ]}
      />
    </TableWrapper>
  );
};
